import {cn} from '@sollapay/ui';
import {AiBadge, Button, EmptyState, Icon, Typography} from '@sollapay/ui/components';

import {RosterPaneLoading, RosterSidebarLoadingList} from './RosterLoadingState';
import {RosterStatusBadge} from './RosterStatusBadge';

import type {RosterStatus} from './RosterStatusBadge';
import type {FC, ReactNode} from 'react';

export interface RosterSidebarItem {
  id: string;
  title: string;
  subtitle?: string;
  aiBadgeLabel?: string;
  status?: RosterStatus;
}

export interface RosterLoadingCopy {
  sidebarLabel: string;
  title: string;
  subtitle: string;
}

export interface RosterSidebarEmptyState {
  title: string;
  subtitle: string;
}

const BORDERS_CLASS = {y: 'border-y', top: 'border-t', none: ''} as const;

export interface RosterSidebarLayoutProps {
  title: string;
  items: RosterSidebarItem[];
  selectedIndex: number | null;
  onSelect: (index: number) => void;
  onAdd: () => void;
  addLabel: string;
  addDisabled?: boolean;
  summary?: string;
  loading?: RosterLoadingCopy;
  emptyState: RosterSidebarEmptyState;
  borders?: keyof typeof BORDERS_CLASS;
  className?: string;
  children: ReactNode;
}

/** Master-detail shell for a repeater (buyers, sellers, …): row list in a sidebar, selected row's form in the pane. */
export const RosterSidebarLayout: FC<RosterSidebarLayoutProps> = ({
  title,
  items,
  selectedIndex,
  onSelect,
  onAdd,
  addLabel,
  addDisabled = false,
  summary,
  loading,
  emptyState,
  borders = 'y',
  className,
  children
}) => {
  const isEmpty = items.length === 0;
  const loadingCopy = isEmpty ? loading : undefined;

  return (
    <div
      data-slot="roster-sidebar-layout"
      className={cn('flex min-h-96 border-border-subtle', BORDERS_CLASS[borders], className)}
    >
      <aside
        data-slot="roster-sidebar"
        className="w-68 shrink-0 border-e border-border-subtle bg-background-secondary"
      >
        <div data-slot="roster-sidebar-content" className="sticky top-0 flex flex-col gap-4 p-4">
          <Typography size="sm" weight="medium" color="tertiary">
            {title}
          </Typography>
          <RosterSidebarBody
            items={items}
            selectedIndex={selectedIndex}
            onSelect={onSelect}
            loading={loadingCopy}
          />
          <Button
            variant="secondary"
            icon={<Icon name="invite-user" width={20} height={20} />}
            onClick={onAdd}
            disabled={!!loadingCopy || addDisabled}
          >
            {addLabel}
          </Button>
          {summary && (
            <Typography
              size="xs"
              color="secondary"
              className="leading-snug"
              data-slot="roster-summary"
            >
              {summary}
            </Typography>
          )}
        </div>
      </aside>
      <div data-slot="roster-pane" className="flex min-w-0 flex-1 flex-col p-6">
        <RosterPaneBody
          isEmpty={isEmpty}
          loading={loadingCopy}
          emptyState={emptyState}
          addAction={
            <Button onClick={onAdd} disabled={addDisabled}>
              {addLabel}
            </Button>
          }
        >
          {children}
        </RosterPaneBody>
      </div>
    </div>
  );
};

interface RosterPaneBodyProps {
  isEmpty: boolean;
  loading: RosterLoadingCopy | undefined;
  emptyState: RosterSidebarEmptyState;
  addAction: ReactNode;
  children: ReactNode;
}

const RosterPaneBody: FC<RosterPaneBodyProps> = ({
  isEmpty,
  loading,
  emptyState,
  addAction,
  children
}) => {
  if (loading) return <RosterPaneLoading title={loading.title} subtitle={loading.subtitle} />;

  if (isEmpty) {
    return (
      <EmptyState
        size="sm"
        className="flex-1 justify-center"
        showGrid={false}
        title={emptyState.title}
        subtitle={emptyState.subtitle}
        illustration={<Icon name="users" className="size-7 text-fg-tertiary" />}
        action={addAction}
      />
    );
  }

  return children;
};

interface RosterSidebarBodyProps {
  items: RosterSidebarItem[];
  selectedIndex: number | null;
  onSelect: (index: number) => void;
  loading: RosterLoadingCopy | undefined;
}

const RosterSidebarBody: FC<RosterSidebarBodyProps> = ({
  items,
  selectedIndex,
  onSelect,
  loading
}) => {
  if (loading) return <RosterSidebarLoadingList label={loading.sidebarLabel} />;

  if (items.length === 0) return null;

  return (
    <div
      data-slot="roster-sidebar-list"
      className="flex flex-col divide-y divide-border-subtle overflow-hidden rounded-xl border border-border-subtle bg-background-surface"
    >
      {items.map((item, index) => (
        <RosterSidebarCard
          key={item.id}
          item={item}
          selected={index === selectedIndex}
          onSelect={() => onSelect(index)}
        />
      ))}
    </div>
  );
};

interface RosterSidebarCardProps {
  item: RosterSidebarItem;
  selected: boolean;
  onSelect: () => void;
}

const RosterSidebarCard: FC<RosterSidebarCardProps> = ({item, selected, onSelect}) => (
  <Button
    variant="tertiary"
    aria-pressed={selected}
    onClick={onSelect}
    data-slot="roster-sidebar-card"
    className={cn(
      'relative h-auto w-full justify-between gap-3 overflow-hidden rounded-none px-4 py-3 text-start focus-visible:-outline-offset-2',
      selected &&
        'bg-background-surface_hover before:absolute before:inset-y-0 before:inset-s-0 before:w-0.75 before:bg-brand-primary'
    )}
  >
    <span className="flex min-w-0 flex-1 flex-col">
      <Typography as="span" size="sm" weight="medium" color={selected ? undefined : 'secondary'}>
        {item.title}
      </Typography>
      {item.subtitle && (
        <Typography as="span" size="xs" color="tertiary">
          {item.subtitle}
        </Typography>
      )}
    </span>
    {item.aiBadgeLabel ? (
      <AiBadge state="generating">{item.aiBadgeLabel}</AiBadge>
    ) : (
      item.status && <RosterStatusBadge {...item.status} />
    )}
  </Button>
);
